import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Search,
  UserCircle,
  Trash2,
  Edit,
  Eye,
  UserCheck,
  UserX,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  Button,
  Input,
  Table,
  Badge,
  Pagination,
  ConfirmModal,
  Select,
  Card,
} from "@/components/ui";
import { usersApi } from "@/services/api";
import { useAuthStore } from "@/stores/authStore";

const ROLE_LABELS: Record<string, string> = {
  super_admin: "Super Admin",
  admin: "Administrador",
  manager: "Gerente",
  seller: "Vendedor",
  client: "Cliente",
};

const ROLE_COLORS: Record<string, "success" | "warning" | "danger" | "info"> = {
  super_admin: "danger",
  admin: "danger",
  manager: "warning",
  seller: "info",
  client: "success",
};

export default function UsersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuthStore();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);

  const limit = 20;

  const { data, isLoading } = useQuery({
    queryKey: ["users", page, search, roleFilter],
    queryFn: () =>
      usersApi.list({
        page,
        limit,
        search,
        role: roleFilter,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: usersApi.delete,
    onSuccess: () => {
      toast.success("Usuário deletado com sucesso");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setDeleteUserId(null);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Erro ao deletar usuário");
    },
  });

  const users = (data as any)?.data || [];
  const paginationData = (data as any)?.pagination;

  const transformedUsers = users.map((user: any) => ({
    ...user,
    is_active:
      user.is_active === 1 || user.is_active === "1" || user.is_active === true,
  }));

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleRoleFilter = (value: string) => {
    setRoleFilter(value);
    setPage(1);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Nunca";
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns = [
    {
      key: "name",
      header: "Nome",
      render: (user: any) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center text-white font-semibold">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-medium text-text-primary">{user.name}</p>
            <p className="text-sm text-text-muted">{user.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "role",
      header: "Função",
      render: (user: any) => (
        <Badge variant={(ROLE_COLORS[user.role] || "success") as any}>
          {ROLE_LABELS[user.role] ||
            ROLE_LABELS[String(user.role).toLowerCase()] ||
            user.role}
        </Badge>
      ),
    },
    {
      key: "client_name",
      header: "Cliente Vinculado",
      render: (user: any) =>
        user.client_name ? (
          <span className="text-text-secondary">{user.client_name}</span>
        ) : (
          <span className="text-text-muted italic">N/A</span>
        ),
    },
    {
      key: "is_active",
      header: "Status",
      render: (user: any) => (
        <div className="flex items-center gap-2">
          {user.is_active ? (
            <>
              <UserCheck className="w-4 h-4 text-green-400" />
              <span className="text-green-400">Ativo</span>
            </>
          ) : (
            <>
              <UserX className="w-4 h-4 text-red-400" />
              <span className="text-red-400">Inativo</span>
            </>
          )}
        </div>
      ),
    },
    {
      key: "last_login_at",
      header: "Último Login",
      render: (user: any) => (
        <span className="text-text-secondary text-sm">
          {formatDate(user.last_login_at)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Ações",
      render: (user: any) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/admin/users/${user.id}`)}
            title="Visualizar usuário"
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/admin/users/${user.id}/edit`)}
            disabled={user.id === currentUser?.id}
            title="Editar usuário"
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteUserId(user.id)}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            disabled={user.id === currentUser?.id}
            title="Deletar usuário"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary flex items-center gap-2 sm:gap-3">
            <UserCircle className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
            Usuários
          </h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">
            Gerencie os usuários do sistema
          </p>
        </div>
        <Button
          onClick={() => navigate("/admin/users/new")}
          className="w-full sm:w-auto"
        >
          <Plus className="w-5 h-5 mr-2" />
          Novo Usuário
        </Button>
      </div>

      {/* Filtros */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Buscar por nome ou email..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              leftIcon={<Search className="w-5 h-5" />}
            />
          </div>
          <div className="w-full sm:w-64">
            <Select
              value={roleFilter}
              onChange={(e) => handleRoleFilter(e.target.value)}
              options={[
                { value: "", label: "Todas as funções" },
                { value: "admin", label: "Administrador" },
                { value: "manager", label: "Gerente" },
                { value: "seller", label: "Vendedor" },
                { value: "client", label: "Cliente" },
              ]}
            />
          </div>
        </div>
      </Card>

      {/* Tabela */}
      <div className="bg-dark-card rounded-2xl border border-white/5 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-text-muted">
            <UserCircle className="w-16 h-16 mb-4 opacity-50" />
            <p className="text-lg">
              {search || roleFilter
                ? "Nenhum usuário encontrado"
                : "Nenhum usuário cadastrado"}
            </p>
            {!search && !roleFilter && (
              <Button
                className="mt-4"
                onClick={() => navigate("/admin/users/new")}
              >
                <Plus className="w-5 h-5 mr-2" />
                Cadastrar Primeiro Usuário
              </Button>
            )}
          </div>
        ) : (
          <>
            <Table
              data={transformedUsers}
              columns={columns}
              keyExtractor={(user: any) => user.id}
            />
            {paginationData && paginationData.pages > 1 && (
              <div className="p-4 border-t border-white/5">
                <Pagination
                  currentPage={page}
                  totalPages={paginationData.pages}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmModal
        isOpen={!!deleteUserId}
        onClose={() => setDeleteUserId(null)}
        onConfirm={() => deleteUserId && deleteMutation.mutate(deleteUserId)}
        title="Deletar Usuário"
        message="Tem certeza que deseja deletar este usuário? Esta ação não pode ser desfeita."
        confirmText="Deletar"
        cancelText="Cancelar"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
